import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Form1099LTC } from "./interface/1099LTC-interface";
import { personNameControl } from "../../constants/recipientIdentity";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import { formatDate } from "../../constants/formatDate";
import { parseAmount } from "../../constants/validationFunctions";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";

// Helper function to format amounts for IRS (round to whole dollar)
function formatIrsAmount(amount: string | number): string {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;
  // Convert to integer by removing decimal places (round to whole dollar)
  return Math.round(numericAmount).toString();
}


/**
 * The policyholder's surname. A split record (a Pub 1220 upload) carries it in
 * POLICYHOLDER_NAME2; a legacy record has one name column, which used to be written as both
 * the first and the last name, and still is. Writing POLICYHOLDER_NAME twice for a split
 * record filed "CAROL CAROL" for Carol Irwin.
 */
function policyholderLastName(record: Record<string, any>): string {
  return record.POLICYHOLDER_NAME2 || record.POLICYHOLDER_NAME;
}

/** Pub 1220 name control: the first four letters of the surname, not of the first name. */
function policyholderNameControl(record: Record<string, any>): string {
  return personNameControl(record, record.POLICYHOLDER_NAME || "", policyholderLastName(record) || "");
}

export async function generate1099LTCXml(
  ctx: EngineContext,records: Form1099LTC[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Determine tax year - use 2025 for 2025 schema
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);
  // Generate proper UniqueTransmissionId
  const uniqueId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  const form1099LTCDetails = records.map((record) => {
    // Validate and force a valid state code for US addresses
    const validPolicyholderState =
      record.POLICYHOLDER_STATE?.substring(0, 2) || "XX";
    const validInsuredState = record.INSURED_STATE?.substring(0, 2) || "XX";

    // Map invalid state codes to valid ones
    const stateCodeMap: { [key: string]: string } = {
      ON: "OH", // Example mapping, adjust based on your needs
      // Add other mappings as needed
    };

    const mappedPolicyholderState =
      stateCodeMap[validPolicyholderState] || validPolicyholderState;
    const mappedInsuredState =
      stateCodeMap[validInsuredState] || validInsuredState;

    return {
      TaxYr: taxYear,
      RecordId: record.id.toString(),
      IssuerOfficeCd: "ABCD",
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
      RecipientDetail: {
        TIN: ctx.irs.testIndicator
          ? "000000000"
          : record.POLICYHOLDER_TAX_ID?.replace(/-/g, ""),
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        PersonNameControlTxt: policyholderNameControl(record),
        PersonName: {
          PersonFirstNm: record.POLICYHOLDER_NAME,
          PersonLastNm: policyholderLastName(record),
        },
        MailingAddressGrp: {
          USAddress: {
            AddressLine1Txt: record.POLICYHOLDER_STREET_ADDRESS_1,
            ...(record.POLICYHOLDER_STREET_ADDRESS_2 && {
              AddressLine2Txt: record.POLICYHOLDER_STREET_ADDRESS_2,
            }),
            CityNm: record.POLICYHOLDER_CITY,
            StateAbbreviationCd: mappedPolicyholderState,
            ZIPCd: record.POLICYHOLDER_ZIP_CODE,
          },
        },
      },
      ...(record.INSURED_ACCOUNT_NUMBER && {
        RecipientAccountNum: record.INSURED_ACCOUNT_NUMBER,
      }),
      ...(record.BOX1_GROSS_BENEFITS_PAID && {
        GrossLTCBenefitPaidAmt: formatIrsAmount(
          parseAmount(record.BOX1_GROSS_BENEFITS_PAID)
        ),
      }),
      ...(record.BOX2_ACCELERATED_DEATH_BENEFITS_PAID && {
        AcceleratedDeathBenefitPaidAmt: formatIrsAmount(
          parseAmount(record.BOX2_ACCELERATED_DEATH_BENEFITS_PAID)
        ),
      }),
      PerDiemInd: record.PER_DIEM === "X" ? "1" : "0",
      ReimbursedAmountInd: record.REIMBURSED_AMOUNT === "X" ? "1" : "0",
      InsuredGrp: {
        TIN: (record.INSURED_SSN || "").replace(/-/g, ""),
        PersonName: {
          PersonFirstNm: record.INSURED_NAME,
          PersonLastNm: record.INSURED_NAME || "UNKNOWN",
        },
        MailingAddressGrp: {
          USAddress: {
            AddressLine1Txt: record.INSURED_STREET_ADDRESS_1,
            ...(record.INSURED_STREET_ADDRESS_2 && {
              AddressLine2Txt: record.INSURED_STREET_ADDRESS_2,
            }),
            CityNm: record.INSURED_CITY,
            StateAbbreviationCd: mappedInsuredState,
            ZIPCd: record.INSURED_ZIP_CODE,
          },
        },
      },
      QualifiedContractInd: record.QUALIFIED_CONTRACT === "X" ? "1" : "0",
      LTCInsuredChronicallyIllInd: record.CHRONICALLY_ILL === "X" ? "1" : "0",
      LTCInsuredTerminallyIllInd: record.TERMINAL_ILL === "X" ? "1" : "0",
      ...(record.DATE_CERTIFIED && {
        CertifiedDt: formatDate(new Date(record.DATE_CERTIFIED)),
      }),
    };
  });

  const xmlObj = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: records.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099LTC",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: formatDate(new Date()),
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: records.length.toString(),
            IRSubmission1FormTotals: {
              Form1099LTCTotalAmtGrp: calculateTotals(records),
            },
          },
          IRSubmission1Detail: {
            Form1099LTCDetail: form1099LTCDetails,
          },
        },
      ],
    },
  };

  const xml = builder.build(xmlObj);
  return { xml, taxYear };
}

// Helper function to calculate totals for 1099-LTC records
// According to the 2025 XSD schema for Form1099LTCTotalAmtGrpType
function calculateTotals(records: Form1099LTC[]): {
  GrossLTCBenefitPaidAmt: string;
  AcceleratedDeathBenefitPaidAmt: string;
} {
  // Initialize totals object - order matches 2025 schema
  const totals = {
    GrossLTCBenefitPaidAmt: "0",
    AcceleratedDeathBenefitPaidAmt: "0",
  };

  // Calculate actual totals from records
  records.forEach((record) => {
    if (record.BOX1_GROSS_BENEFITS_PAID) {
      const amount = parseFloat(parseAmount(record.BOX1_GROSS_BENEFITS_PAID));
      totals.GrossLTCBenefitPaidAmt = formatIrsAmount(
        parseFloat(totals.GrossLTCBenefitPaidAmt) + amount
      );
    }

    if (record.BOX2_ACCELERATED_DEATH_BENEFITS_PAID) {
      const amount = parseFloat(
        parseAmount(record.BOX2_ACCELERATED_DEATH_BENEFITS_PAID)
      );
      totals.AcceleratedDeathBenefitPaidAmt = formatIrsAmount(
        parseFloat(totals.AcceleratedDeathBenefitPaidAmt) + amount
      );
    }
  });

  return totals;
}

